import { iPublication, Publication } from '../models/Publication';
import axios, { AxiosResponse } from 'axios';
import { iSearch__API, HttpStatusCode } from '../apiSchema';
import { getApiLink } from '../api';
import { useDebouncedData } from '../hooks/useDebouncedData';
import { MutableRefObject, Ref, useRef } from 'react';

interface iSearchResultsData {
    docsResults: iPublication[];
    docsResultsCount: number;
    macsResults: iPublication[];
    macsResultsCount: number;
    generatedText: string;
    statusCode?: number;
}

const defaultData: iSearchResultsData = {
    docsResults: [],
    docsResultsCount: 0,
    macsResults: [],
    macsResultsCount: 0,
    generatedText: ''
};

interface iGetSearchResults {
    takeMacs?: number;
    takeDocs?: number;
    skipMacs?: number;
    skipDocs?: number;
}

async function getSearchResults(
    query: string,
    inMyLibrary: boolean,
    reqData: iGetSearchResults,
    controller: MutableRefObject<AbortController>
): Promise<iSearchResultsData> {
    controller.current.abort();
    controller.current = new AbortController();
    const { takeMacs = 0, takeDocs = 0, skipMacs = 0, skipDocs = 0 } = reqData;

    if (query.trim() === '') return defaultData;
    if (!takeMacs && !takeDocs) return defaultData;
    const { signal } = controller.current;
    const promise = axios.post(
        getApiLink('PublicationClient/Search'),
        {
            query,
            inMyLibrary,
            skipMacs,
            takeMacs,
            skipDocs,
            takeDocs
        },
        {
            signal
        }
    );

    let response: AxiosResponse<iSearch__API>;
    try {
        response = await promise;
    } catch (err) {
        if (signal.aborted) return new Promise<iSearchResultsData>(() => {});
        throw err;
    }

    const { data } = response;

    const { macsResults, macsResultsCount = 0, documentsResults, otherResultsCount = 0, generatedText = '', statusCode } = data;

    return {
        docsResultsCount: otherResultsCount,
        macsResultsCount,
        docsResults: (documentsResults || [])
            .map((i) => new Publication({ apiModel: i }))
            .sort((a, b) => (b.score || 0) - (a.score || 0)),
        macsResults: (macsResults || [])
            .map((i) => new Publication({ apiModel: i }))
            .sort((a, b) => (b.score || 0) - (a.score || 0)),
        generatedText: generatedText || '',
        statusCode
    };
}

export default function useSearchResults(query: string, inMyLibrary: boolean, reqData: iGetSearchResults) {
    const controller = useRef<AbortController>(new AbortController());
    return useDebouncedData(300, defaultData, getSearchResults, query, inMyLibrary, reqData, controller);
}
